import { ReactElement, useCallback, useMemo } from 'react';
import { CommonTranslationKeys } from 'intl/keys';
import { useCurrentIssue } from 'components/dataProviders/withIssue';
import { useProjectId } from 'store/project';
import { useIssueRouteParams } from 'hooks/navigation/useRouteParams';
import { getLastSuccessfullVersion } from 'shared/domain/documentation/documentationVersionHelpers';
import { useImportFromDocumentation } from './ImportFromDocumentationContext';
import { useImportFromDocumentationBrowser } from './useImportFromDocumentationBrowser';
import { DocumentationBrowserDialog } from 'components/documentation/DocumentationBrowser';
import { useIssueDocumentationOperations } from 'store/issue';
import { showErrorToastAndLog } from 'services/toaster';

export function ImportFromDocumentationDialog(): ReactElement {
  const issue = useCurrentIssue();
  const selectedProjectId = useProjectId();
  const { issueId, projectId } = useIssueRouteParams();

  const { isOpen, closeImportDialog } = useImportFromDocumentation();
  const browser = useImportFromDocumentationBrowser(isOpen);
  const { importFromDocumentation, importPending } =
    useIssueDocumentationOperations();

  const resolvedProjectId = projectId ?? selectedProjectId;
  const resolvedIssueId = issueId ?? issue?._id;

  const selectedImportCandidate = useMemo(() => {
    const documentation = browser.selectedDocumentation;
    if (!documentation?._id) return undefined;

    const version = getLastSuccessfullVersion(documentation);
    if (!version?._id) return undefined;

    return {
      documentationId: documentation._id,
      versionId: version._id,
    };
  }, [browser.selectedDocumentation]);

  const handleImport = useCallback(() => {
    if (!issue || !resolvedProjectId || !resolvedIssueId) return;

    if (!selectedImportCandidate) {
      closeImportDialog();
      return;
    }

    importFromDocumentation({
      issue,
      issueId: resolvedIssueId,
      documentationId: selectedImportCandidate.documentationId,
      versionId: selectedImportCandidate.versionId,
    })
      .catch((err) => {
        showErrorToastAndLog('Import from documentation failed',
          err
        );
      })
      .finally(() => {
        closeImportDialog();
      });
  }, [
    issue,
    resolvedProjectId,
    resolvedIssueId,
    selectedImportCandidate,
    closeImportDialog,
    importFromDocumentation,
  ]);

  const isImportDisabled =
    browser.loading ||
    Boolean(browser.loadError) ||
    !selectedImportCandidate ||
    !resolvedIssueId ||
    !resolvedProjectId;

  return (
    <DocumentationBrowserDialog
      isOpen={isOpen}
      onClose={closeImportDialog}
      titleFallbackId={CommonTranslationKeys.chooseFromDocumentation}
      searchDataQa='import-from-documentation-search'
      browser={browser}
      confirmLabelId={CommonTranslationKeys.import}
      onConfirm={handleImport}
      confirmDisabled={isImportDisabled}
      confirmLoading={importPending || browser.loading}
    />
  );
}
